import { useState, useMemo } from 'react';
import {
  Typography,
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Modal,
  Form,
  Popconfirm,
  message,
  Row,
  Col,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  LockOutlined,
  StopOutlined,
  CheckCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  useUsers,
  useCreateUser,
  useUpdateUser,
  useToggleActive,
  useResetPassword,
  useRoles,
} from '@hooks/useAdmin';
import type {
  UserListItem,
  UserFilters,
  CreateUserRequest,
  UpdateUserRequest,
} from '@/types/admin';

const acqRoleColors: Record<string, string> = {
  Admin: 'red',
  Director: 'purple',
  StorekeeperManager: 'blue',
  SalesManager: 'green',
  PurchaseManager: 'orange',
  ServiceEngineer: 'cyan',
  Accountant: 'gold',
};

export default function UsersPage() {
  const [acqFilters, setMdFilters] = useState<UserFilters>({
    page: 1,
    pageSize: 20,
  });
  const [acqSearchText, setMdSearchText] = useState('');
  const [acqModalOpen, setMdModalOpen] = useState(false);
  const [acqEditingUser, setMdEditingUser] = useState<UserListItem | null>(null);
  const [acqForm] = Form.useForm();

  const { data: acqUsersData, isLoading: acqIsLoading } = useUsers(acqFilters);
  const { data: acqRolesList } = useRoles();
  const acqCreateUser = useCreateUser();
  const acqUpdateUser = useUpdateUser();
  const acqToggleActive = useToggleActive();
  const acqResetPassword = useResetPassword();

  const acqIsCreating = !acqEditingUser;

  const acqHandleSearch = () => {
    setMdFilters((acqPrev) => ({ ...acqPrev, search: acqSearchText || undefined, page: 1 }));
  };

  const acqHandleRoleFilter = (acqRole: string | undefined) => {
    setMdFilters((acqPrev) => ({ ...acqPrev, role: acqRole, page: 1 }));
  };

  const acqHandleActiveFilter = (acqValue: string | undefined) => {
    let acqIsActive: boolean | undefined;
    if (acqValue === 'true') acqIsActive = true;
    else if (acqValue === 'false') acqIsActive = false;
    setMdFilters((acqPrev) => ({ ...acqPrev, isActive: acqIsActive, page: 1 }));
  };

  const acqHandleOpenCreate = () => {
    setMdEditingUser(null);
    acqForm.resetFields();
    setMdModalOpen(true);
  };

  const acqHandleOpenEdit = (acqRecord: UserListItem) => {
    setMdEditingUser(acqRecord);
    acqForm.setFieldsValue({
      firstName: acqRecord.firstName,
      lastName: acqRecord.lastName,
      middleName: acqRecord.middleName ?? '',
      email: acqRecord.email,
      roles: acqRecord.roles,
    });
    setMdModalOpen(true);
  };

  const acqHandleModalOk = async () => {
    try {
      const acqValues = await acqForm.validateFields();
      if (acqIsCreating) {
        const acqRequest: CreateUserRequest = {
          userName: acqValues.userName,
          email: acqValues.email,
          password: acqValues.password,
          firstName: acqValues.firstName,
          lastName: acqValues.lastName,
          middleName: acqValues.middleName || undefined,
          roles: acqValues.roles ?? [],
        };
        await acqCreateUser.mutateAsync(acqRequest);
        message.success('Користувача створено');
      } else {
        const acqRequest: UpdateUserRequest = {
          id: acqEditingUser!.id,
          firstName: acqValues.firstName,
          lastName: acqValues.lastName,
          middleName: acqValues.middleName || undefined,
          email: acqValues.email,
          phoneNumber: acqValues.phoneNumber || undefined,
          roles: acqValues.roles ?? [],
        };
        await acqUpdateUser.mutateAsync(acqRequest);
        message.success('Користувача оновлено');
      }
      setMdModalOpen(false);
      acqForm.resetFields();
    } catch (acqErr) {
      // validation errors are handled by Form
      if (acqErr && typeof acqErr === 'object' && 'errorFields' in acqErr) return;
      message.error('Помилка збереження');
    }
  };

  const acqHandleToggleActive = async (acqRecord: UserListItem) => {
    try {
      await acqToggleActive.mutateAsync(acqRecord.id);
      message.success(
        acqRecord.isActive ? 'Користувача деактивовано' : 'Користувача активовано'
      );
    } catch {
      message.error('Помилка зміни статусу');
    }
  };

  const acqHandleResetPassword = async (acqRecord: UserListItem) => {
    try {
      const acqResult = await acqResetPassword.mutateAsync(acqRecord.id);
      Modal.info({
        title: 'Новий пароль',
        content: (
          <div>
            <p>Новий пароль для <strong>{acqRecord.userName}</strong>:</p>
            <Typography.Text code copyable>
              {acqResult.newPassword}
            </Typography.Text>
          </div>
        ),
      });
    } catch {
      message.error('Помилка скидання пароля');
    }
  };

  const acqColumns: ColumnsType<UserListItem> = useMemo(
    () => [
      {
        title: 'Логін',
        dataIndex: 'userName',
        key: 'userName',
        width: 140,
      },
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
        width: 200,
      },
      {
        title: "Ім'я",
        key: 'name',
        width: 200,
        render: (_: unknown, acqRecord: UserListItem) =>
          `${acqRecord.lastName} ${acqRecord.firstName}${acqRecord.middleName ? ' ' + acqRecord.middleName : ''}`,
      },
      {
        title: 'Ролі',
        dataIndex: 'roles',
        key: 'roles',
        width: 200,
        render: (acqRoles: string[]) =>
          acqRoles.map((acqRole) => (
            <Tag key={acqRole} color={acqRoleColors[acqRole] ?? 'default'}>
              {acqRole}
            </Tag>
          )),
      },
      {
        title: 'Активний',
        dataIndex: 'isActive',
        key: 'isActive',
        width: 100,
        render: (acqIsActive: boolean) =>
          acqIsActive ? (
            <Tag color="green">Активний</Tag>
          ) : (
            <Tag color="red">Неактивний</Tag>
          ),
      },
      {
        title: 'Останній вхід',
        dataIndex: 'lastLoginAt',
        key: 'lastLoginAt',
        width: 160,
        render: (acqDate: string | undefined) =>
          acqDate ? dayjs(acqDate).format('DD.MM.YYYY HH:mm') : '—',
      },
      {
        title: 'Дії',
        key: 'actions',
        width: 200,
        render: (_: unknown, acqRecord: UserListItem) => (
          <Space>
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => acqHandleOpenEdit(acqRecord)}
            >
              Редагувати
            </Button>
            <Popconfirm
              title={
                acqRecord.isActive
                  ? 'Деактивувати користувача?'
                  : 'Активувати користувача?'
              }
              onConfirm={() => acqHandleToggleActive(acqRecord)}
              okText="Так"
              cancelText="Ні"
            >
              <Button
                size="small"
                icon={acqRecord.isActive ? <StopOutlined /> : <CheckCircleOutlined />}
                danger={acqRecord.isActive}
              />
            </Popconfirm>
            <Popconfirm
              title="Скинути пароль?"
              description="Буде згенеровано новий пароль"
              onConfirm={() => acqHandleResetPassword(acqRecord)}
              okText="Так"
              cancelText="Ні"
            >
              <Button size="small" icon={<LockOutlined />} />
            </Popconfirm>
          </Space>
        ),
      },
    ],
    []
  );

  return (
    <>
      <Typography.Title level={4} style={{ marginBottom: 16 }}>
        Управління користувачами
      </Typography.Title>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col flex="auto">
          <Space>
            <Input
              placeholder="Пошук..."
              prefix={<SearchOutlined />}
              value={acqSearchText}
              onChange={(acqE) => setMdSearchText(acqE.target.value)}
              onPressEnter={acqHandleSearch}
              style={{ width: 250 }}
              allowClear
            />
            <Select
              placeholder="Роль"
              allowClear
              style={{ width: 180 }}
              onChange={acqHandleRoleFilter}
              options={
                acqRolesList?.map((acqR) => ({ label: acqR, value: acqR })) ?? []
              }
            />
            <Select
              placeholder="Статус"
              allowClear
              style={{ width: 140 }}
              onChange={acqHandleActiveFilter}
              options={[
                { label: 'Активні', value: 'true' },
                { label: 'Неактивні', value: 'false' },
              ]}
            />
            <Button onClick={acqHandleSearch}>Шукати</Button>
          </Space>
        </Col>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={acqHandleOpenCreate}>
            Створити користувача
          </Button>
        </Col>
      </Row>

      <Table<UserListItem>
        rowKey="id"
        columns={acqColumns}
        dataSource={acqUsersData?.items ?? []}
        loading={acqIsLoading}
        pagination={{
          current: acqFilters.page,
          pageSize: acqFilters.pageSize,
          total: acqUsersData?.totalCount ?? 0,
          showSizeChanger: true,
          showTotal: (acqTotal) => `Всього: ${acqTotal}`,
          onChange: (acqPage, acqSize) =>
            setMdFilters((acqPrev) => ({ ...acqPrev, page: acqPage, pageSize: acqSize })),
        }}
        scroll={{ x: 1200 }}
        size="middle"
      />

      <Modal
        title={acqIsCreating ? 'Створити користувача' : 'Редагувати користувача'}
        open={acqModalOpen}
        onOk={acqHandleModalOk}
        onCancel={() => {
          setMdModalOpen(false);
          acqForm.resetFields();
        }}
        confirmLoading={acqCreateUser.isPending || acqUpdateUser.isPending}
        okText="Зберегти"
        cancelText="Скасувати"
        width={520}
        destroyOnClose
      >
        <Form form={acqForm} layout="vertical" style={{ marginTop: 16 }}>
          {acqIsCreating && (
            <>
              <Form.Item
                name="userName"
                label="Логін"
                rules={[{ required: true, message: 'Введіть логін' }]}
              >
                <Input maxLength={100} />
              </Form.Item>
              <Form.Item
                name="password"
                label="Пароль"
                rules={[
                  { required: true, message: 'Введіть пароль' },
                  { min: 8, message: 'Мінімум 8 символів' },
                ]}
              >
                <Input.Password />
              </Form.Item>
            </>
          )}
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Введіть email' },
              { type: 'email', message: 'Невалідний email' },
            ]}
          >
            <Input maxLength={200} />
          </Form.Item>
          <Form.Item
            name="lastName"
            label="Прізвище"
            rules={[{ required: true, message: 'Введіть прізвище' }]}
          >
            <Input maxLength={100} />
          </Form.Item>
          <Form.Item
            name="firstName"
            label="Ім'я"
            rules={[{ required: true, message: "Введіть ім'я" }]}
          >
            <Input maxLength={100} />
          </Form.Item>
          <Form.Item name="middleName" label="По-батькові">
            <Input maxLength={100} />
          </Form.Item>
          {!acqIsCreating && (
            <Form.Item name="phoneNumber" label="Телефон">
              <Input maxLength={20} />
            </Form.Item>
          )}
          <Form.Item
            name="roles"
            label="Ролі"
            rules={[{ required: true, message: 'Оберіть хоча б одну роль' }]}
          >
            <Select
              mode="multiple"
              placeholder="Оберіть ролі"
              options={
                acqRolesList?.map((acqR) => ({ label: acqR, value: acqR })) ?? []
              }
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
